import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  TextField, List, ListItem, ListItemIcon, ListItemText, Checkbox,
  CircularProgress, Typography, FormControlLabel,
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const SelectContactsModal = ({ open, onClose, contactListId, onSaved }) => {
  const [search, setSearch] = useState("");
  const [tags, setTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState({});
  const [selectAll, setSelectAll] = useState(false);

  const tagIds = selectedTags.map((t) => t.id);
  const tagKey = tagIds.join(",");

  const fetchContacts = useCallback(async (pageNumber, searchParam, tagsParam, append) => {
    setLoading(true);
    try {
      const { data } = await api.get("/contact-list-items/contacts", {
        params: { searchParam, pageNumber, tagIds: tagsParam },
      });
      setContacts((prev) => (append ? [...prev, ...data.contacts] : data.contacts));
      setHasMore(data.hasMore);
      setCount(data.count);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  // ao abrir: reseta e carrega as tags
  useEffect(() => {
    if (!open) return;
    setSelected({});
    setSelectAll(false);
    setSearch("");
    setSelectedTags([]);
    setPage(1);
    api
      .get("/tags/list")
      .then(({ data }) => setTags(Array.isArray(data) ? data : []))
      .catch(toastError);
  }, [open]);

  // busca com debounce, refeita quando muda texto ou tags
  useEffect(() => {
    if (!open) return;
    setSelectAll(false);
    const t = setTimeout(() => {
      setPage(1);
      fetchContacts(1, search, tagKey, false);
    }, 400);
    return () => clearTimeout(t);
  }, [search, tagKey, open, fetchContacts]);

  const loadMore = () => {
    const next = page + 1;
    setPage(next);
    fetchContacts(next, search, tagKey, true);
  };

  const toggle = (id) => {
    if (selectAll) return;
    setSelected((prev) => {
      const copy = { ...prev };
      copy[id] ? delete copy[id] : (copy[id] = true);
      return copy;
    });
  };

  const selectedIds = Object.keys(selected).map(Number);
  const totalToAdd = selectAll ? count : selectedIds.length;

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = selectAll
        ? { contactListId, selectAll: true, searchParam: search, tagIds }
        : { contactListId, contactIds: selectedIds };
      const { data } = await api.post("/contact-list-items/from-contacts", payload);
      toast.success(
        `${data.created} contato(s) adicionado(s)` +
          (data.skipped ? `, ${data.skipped} ignorado(s) (duplicados ou sem número)` : "")
      );
      onSaved && onSaved();
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Adicionar dos meus contatos</DialogTitle>
      <DialogContent dividers>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          placeholder="Buscar por nome ou número"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Autocomplete
          multiple
          size="small"
          options={tags}
          value={selectedTags}
          onChange={(_, value) => setSelectedTags(value)}
          getOptionLabel={(tag) => tag.name}
          getOptionSelected={(option, value) => option.id === value.id}
          renderInput={(params) => (
            <TextField {...params} variant="outlined" placeholder="Filtrar por tag" />
          )}
          style={{ marginTop: 12 }}
        />
        <FormControlLabel
          style={{ marginTop: 8 }}
          control={
            <Checkbox
              color="primary"
              checked={selectAll}
              disabled={count === 0}
              onChange={(e) => setSelectAll(e.target.checked)}
            />
          }
          label={`Selecionar todos (${count})`}
        />
        <List dense style={{ maxHeight: 340, overflowY: "auto" }}>
          {contacts.map((c) => (
            <ListItem key={c.id} button onClick={() => toggle(c.id)}>
              <ListItemIcon>
                <Checkbox
                  edge="start"
                  checked={selectAll || !!selected[c.id]}
                  disabled={selectAll}
                  disableRipple
                />
              </ListItemIcon>
              <ListItemText primary={c.name} secondary={c.number} />
            </ListItem>
          ))}
          {loading && (
            <div style={{ textAlign: "center", padding: 8 }}>
              <CircularProgress size={24} />
            </div>
          )}
          {!loading && contacts.length === 0 && (
            <Typography align="center" color="textSecondary">
              Nenhum contato encontrado
            </Typography>
          )}
          {hasMore && !loading && (
            <Button fullWidth onClick={loadMore}>Carregar mais</Button>
          )}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button
          color="primary"
          variant="contained"
          disabled={totalToAdd === 0 || saving}
          onClick={handleSave}
        >
          Adicionar ({totalToAdd})
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SelectContactsModal;